import { useEffect, useRef, useState, type ReactNode } from "react";

// ── TimeDock: ONE timeline for every time-indexed context ──
// Replaces the four separate scrubbers (explorer hour / focus hour / compare
// hour / run cycle) that each had their own slider, colors, and a private copy
// of the date formatter. Contexts register "time tracks"; the dock shows the
// primary one with a switcher for the rest, and adds the play/pause + speed
// control the legacy Streamlit timeline had but the SPA never ported.

export interface TimeMarker {
  index: number;
  color: string;
  title: string;
  body?: string;
}

export interface TimeTrack {
  id: string; // "focus" | "explorer" | "run" | "compare"
  label: string; // switcher chip text
  color: string; // accent for slider + headline
  length: number; // number of steps
  index: number;
  setIndex: (i: number) => void;
  /** headline for the current step (e.g. formatted UTC time + intensity) */
  format: (i: number) => string;
  /** step-unit word for the right-hand counter ("Hour", "Cycle", "Point") */
  unit: string;
  /** optional right-side extra (delta chips etc.) */
  detail?: (i: number) => ReactNode;
  /** optional jump-to markers (narrative story beats) */
  markers?: TimeMarker[];
}

/** Shared UTC formatter — the one previously copy-pasted across five files. */
export function fmtUtc(dt: string | null | undefined, withYear = false): string {
  if (!dt) return "";
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(dt) ? dt : dt + "Z");
  return (
    d.toLocaleString("en-US", {
      month: "short", day: "numeric", ...(withYear ? { year: "numeric" } : {}),
      hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC",
    }) + "Z"
  );
}

const SPEEDS = [1, 2, 4] as const;

interface Props {
  tracks: TimeTrack[];
}

export default function TimeDock({ tracks }: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<1 | 2 | 4>(2);
  const [openMarker, setOpenMarker] = useState<TimeMarker | null>(null);

  // active track: explicit choice if still present, else the first (highest
  // priority) registered track
  const active = tracks.find((t) => t.id === activeId) ?? tracks[0] ?? null;
  const activeRef = useRef(active);
  activeRef.current = active;

  // pause when the track composition changes under us
  const trackKey = tracks.map((t) => t.id).join(",");
  useEffect(() => { setPlaying(false); setOpenMarker(null); }, [trackKey]);

  // playback loop — advances the active track, looping at the end
  useEffect(() => {
    if (!playing || !active) return;
    const iv = setInterval(() => {
      const t = activeRef.current;
      if (!t || t.length < 2) return;
      t.setIndex((t.index + 1) % t.length);
    }, 300 / speed);
    return () => clearInterval(iv);
  }, [playing, speed, active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // keyboard: ←/→ step (Shift = ×6), Space play/pause — ignored while typing
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      const t = activeRef.current;
      if (!t) return;
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        const step = (e.key === "ArrowRight" ? 1 : -1) * (e.shiftKey ? 6 : 1);
        t.setIndex(Math.min(Math.max(t.index + step, 0), t.length - 1));
      } else if (e.key === " ") {
        e.preventDefault();
        setPlaying((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!active) return null;
  const i = Math.min(Math.max(active.index, 0), active.length - 1);

  return (
    <div className="scrubber" style={{ borderColor: `${active.color}55` }}>
      {/* story-beat caption */}
      {openMarker && (
        <div
          style={{
            marginBottom: 8, padding: "8px 12px", borderRadius: 8,
            background: `${openMarker.color}1f`, border: `1px solid ${openMarker.color}`,
            font: "400 12px/1.5 Inter, sans-serif", color: "rgba(255,255,255,0.92)",
          }}
        >
          <b style={{ color: openMarker.color }}>{openMarker.title}</b>
          {openMarker.body && <span> — {openMarker.body}</span>}
          <span
            role="button"
            onClick={() => setOpenMarker(null)}
            style={{ float: "right", cursor: "pointer", color: "rgba(255,255,255,0.55)" }}
          >
            ✕
          </span>
        </div>
      )}

      <div className="scrubber-meta">
        <span style={{ display: "flex", gap: 6, alignItems: "center", minWidth: 0 }}>
          {tracks.length > 1 &&
            tracks.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveId(t.id)}
                style={{
                  padding: "2px 8px", borderRadius: 10, cursor: "pointer",
                  border: `1px solid ${t.id === active.id ? t.color : "rgba(255,255,255,0.18)"}`,
                  background: t.id === active.id ? `${t.color}2b` : "transparent",
                  color: t.id === active.id ? "white" : "rgba(255,255,255,0.6)",
                  font: "600 11px Inter, sans-serif", whiteSpace: "nowrap",
                }}
              >
                {t.label}
              </button>
            ))}
          <span style={{ color: "white", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {tracks.length === 1 ? `${active.label} · ` : ""}{active.format(i)}
          </span>
        </span>
        <span className="muted" style={{ whiteSpace: "nowrap" }}>
          {active.detail?.(i)}
          <span style={{ marginLeft: 10 }}>{active.unit} {i + 1} / {active.length}</span>
        </span>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button
          onClick={() => setPlaying((p) => !p)}
          title="Play/pause (Space)"
          style={{
            width: 30, height: 26, borderRadius: 6, cursor: "pointer", flexShrink: 0,
            border: `1px solid ${active.color}`, background: playing ? `${active.color}33` : "rgba(17,24,39,0.7)",
            color: "white", font: "600 12px Inter, sans-serif",
          }}
        >
          {playing ? "⏸" : "▶"}
        </button>
        <button
          onClick={() => setSpeed((s) => (SPEEDS[(SPEEDS.indexOf(s) + 1) % SPEEDS.length]))}
          title="Playback speed"
          style={{
            width: 30, height: 26, borderRadius: 6, cursor: "pointer", flexShrink: 0,
            border: "1px solid rgba(255,255,255,0.2)", background: "rgba(17,24,39,0.7)",
            color: "rgba(255,255,255,0.8)", font: "600 11px Inter, sans-serif",
          }}
        >
          {speed}×
        </button>
        <div style={{ position: "relative", flex: 1, display: "flex", alignItems: "center" }}>
          <input
            type="range" min={0} max={active.length - 1} value={i}
            onChange={(e) => active.setIndex(Number(e.target.value))}
            style={{ width: "100%", accentColor: active.color }}
          />
          {/* story-beat markers over the slider */}
          {(active.markers ?? []).map((m, k) => (
            <span
              key={k}
              title={m.title}
              onClick={() => { active.setIndex(m.index); setOpenMarker(m); }}
              style={{
                position: "absolute", top: -3,
                left: `calc(${(m.index / Math.max(1, active.length - 1)) * 100}% - 4px)`,
                width: 8, height: 8, borderRadius: 2, transform: "rotate(45deg)",
                background: m.color, border: "1px solid rgba(0,0,0,0.5)",
                cursor: "pointer", zIndex: 2,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
